import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartComponentProps, getBaseOption, formatTooltipValue, ECHARTS_OPTS } from './chartConfig';

interface SunburstNode {
  name: string;
  value?: number;
  children?: SunburstNode[];
}

function toSunburstData(data: Record<string, unknown>[]): SunburstNode[] {
  return data.map(d => ({
    name: String(d.name ?? ''),
    value: Number(d.value) || undefined,
    children: Array.isArray(d.children) ? toSunburstData(d.children as Record<string, unknown>[]) : undefined,
  }));
}

export const SunburstChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);
    const sunburstData = toSunburstData(data);

    return {
      ...base,
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number; marker?: string };
          return `<div style="font-size:12px">${p.marker}<b>${p.name}</b><br/>${formatTooltipValue(p.value)}</div>`;
        },
      },
      series: [
        {
          type: 'sunburst',
          data: sunburstData,
          radius: ['15%', '90%'],
          sort: undefined,
          emphasis: { focus: 'ancestor' as const },
          label: { fontSize: 10, rotate: 'radial' as any },
          itemStyle: { borderRadius: 4, borderWidth: 2, borderColor: '#fff' },
          levels: [
            {},
            { r0: '15%', r: '40%', label: { fontSize: 11 }, itemStyle: { borderWidth: 2 } },
            { r0: '40%', r: '65%', label: { fontSize: 10 }, itemStyle: { borderWidth: 1 } },
            { r0: '65%', r: '90%', label: { show: false }, itemStyle: { borderWidth: 1 } },
          ],
        },
      ],
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
